import json from '#shared/util/json.js';
import appError from '#shared/util/appError.js';

import asyncCall from './asyncCall.js';
import avEvents from './avEvents.js';

/**
 * @typedef {object} ActivationOptions
 * @property {string} walletKey Activation code for products with Alpha license
 * @property {string} activationKey Activation code for products without Alpha licences
 * @property {string} correlationID ID generated by IPM for identifying which product was bought by the user
 * @property {string} productSKU Product SKU ID (eg. PRW)
 */

/**
 * @typedef {object} ActivationProduct
 * @property {string} internalId Product internal ID (eg. PRW-00-001-12)
 */

/**
 * @typedef {object} ActivationResultALPHA
 * @property {string} event Activation event (eg. InsertWalletKey_<walletKey>_<productSKU>_<correlationID>)
 * @property {object} response Additional result details
 * @property {string} [response.resultCode] Result of the activation
 * @property {boolean} [response.success] Result of the activation
 */

/**
 * @typedef {object} ActivationResultILEAP
 * @property {object} response Additional result details
 * @property {string} [response.status] Result of the activation (e.g. ACTIVATION_SUCCESS, INSTALLATION_START, INSTALLATION_SUCCESS, INSTALLATION_FAILED, TRANSACTION_COMPLETE, TRANSACTION_FAILED, TRANSACTION_ABORTED)
 * @property {string} [response.product_id] Product id
 */

const activateWithClient = ({ walletKey, activationKey, correlationID = '', productSKU = '' } = {}) => {
	if ((typeof walletKey === 'string' && walletKey || typeof activationKey === 'string' && activationKey) && typeof correlationID === 'string' && typeof productSKU === 'string') {
		// Time before failing the activation
		const activationFailedTimeout = 10_000;
		// ILEAP activation listener
		const ileapListener = new Promise((resolve, reject) => {
			// Wait before failing the ILEAP activation (if listener hasn't been triggered) so the ALPHA activation can be evaluated
			const ileapActivationTimeout = setTimeout(() => reject('ILEAP activation hasn\'t been resolved'), activationFailedTimeout);
			avEvents.addEventListener('app.estore.notify', (event, response) => {
				clearTimeout(ileapActivationTimeout);
				if (!response) {
					reject('app.estore.notify callback - empty response.');
				}
				const responseObject = json.parse(response) || {};
				const resultObject = {
					response: responseObject
				};
				if (['ACTIVATION_SUCCESS', 'INSTALLATION_SUCCESS'].includes(responseObject.status)) {
					resolve(resultObject);
				} else if (!['INSTALLATION_START'].includes(responseObject.status)) {
					reject(resultObject);
				}
			});
		});
		// ALPHA activation listener
		const alphaListener = new Promise((resolve, reject) => {
			const activation = walletKey ? {
				key: walletKey,
				id: correlationID,
				method: 'InsertWalletKey',
				event: `InsertWalletKey_${walletKey}_${productSKU}_${correlationID}`
			} : {
				key: activationKey,
				id: productSKU,
				method: 'ActivateProduct',
				event: `ActivateProduct_${activationKey}_${productSKU}_${correlationID}`
			};
			// Wait before failing the ALPHA activation (if listener hasn't been triggered) so the ILEAP activation can be evaluated
			const alphaActivationTimeout = setTimeout(() => reject('Alpha activation hasn\'t been resolved'), activationFailedTimeout);
			avEvents.addEventListener(activation.event, (event, response) => {
				clearTimeout(alphaActivationTimeout);
				const responseObject = json.parse(response) || {};
				const resultObject = {
					event,
					response: responseObject
				};
				if (responseObject.resultCode === '0') {
					resolve(resultObject);
				} else {
					reject(resultObject);
				}
			});
			asyncCall(activation.method, ([, error]) => {
				if (error) {
					const resultObject = {
						event: activation.event,
						response: json.parse(error) || {}
					};
					reject(resultObject);
				}
			}, [activation.key, activation.id, activation.event, 'silent']);
		});
		return Promise.any([alphaListener, ileapListener]).catch(() => {
			throw new Error('Both ALPHA + ILEAP activation failed.');
		});
	} else {
		throw appError('Invalid method arguments to activateWithClient', {
			code: 'APP_FRONTEND_UTIL',
			origin: 'activateWithClient',
			detail: {
				walletKey,
				activationKey,
				correlationID,
				productSKU
			}
		});
	}
};

/**
 * Initialize ipm platform activation
 * @param {ActivationOptions} options
 * @param {ActivationProduct[]} products
 * @returns {Promise<ActivationResultALPHA|ActivationResultILEAP>}
 */
export default function activateProductIpm(options, products) {
	return activateWithClient(options, products);
}
